// =============================================
// OpenAI LLM Provider
// Uses /api/openai/ proxy (dev) or direct API
// =============================================

import type { LLMProvider, LLMOptions, LLMCallOptions, LLMResponse, ResponsesAPIOptions } from "./types";
import { logDebug, logVerbose } from "../contentEngine/contentLogger";
import { getApiTarget } from "@/lib/apiBase";
import { trackLlmCall } from "./costTracker";

const DEFAULT_MODEL = "gpt-4.1";
const DEFAULT_TEMPERATURE = 0.7;
const DEFAULT_TIMEOUT_MS = 300_000; // 5 min — content engine needs long responses

/** Approximate pricing per 1M tokens (USD). Zrodlo: platform.openai.com/docs/pricing */
const OPENAI_PRICING: Record<string, { input: number; output: number }> = {
  // gpt-5.x family — reasoning models, reasoning_tokens bill jako output
  "gpt-5.4":       { input: 10,  output: 40 },
  "gpt-5.4-mini":  { input: 2,   output: 8 },
  "gpt-5.2":       { input: 2,   output: 8 },    // legacy
  // gpt-4.1 family
  "gpt-4.1":       { input: 2,   output: 8 },
  "gpt-4.1-mini":  { input: 0.4, output: 1.6 },
  "gpt-4.1-nano":  { input: 0.1, output: 0.4 },
  // fallback dla o-series
  "o1":            { input: 15,  output: 60 },
  "o3":            { input: 10,  output: 40 },
  "o3-mini":       { input: 3,   output: 12 },
};

function estimateCost(model: string, tokensIn: number, tokensOut: number): number {
  const pricing = OPENAI_PRICING[model] || OPENAI_PRICING["gpt-4.1"];
  return (tokensIn * pricing.input + tokensOut * pricing.output) / 1_000_000;
}

/**
 * Check if model is a reasoning model (gpt-5.x, o1, o3).
 * Reasoning models: no temperature, no top_p, no max_tokens, use reasoning_effort.
 */
function isReasoningModel(model: string): boolean {
  return model.startsWith("gpt-5") || model.startsWith("o1") || model.startsWith("o3");
}

const RETRY_MAX_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 2000;

/**
 * Fetch with per-attempt timeout + exponential backoff retry for transient errors.
 *
 * Retriable: 5xx, 429 (rate limit — honors Retry-After), 408 (timeout),
 * AbortError (our timeout), network errors (TypeError from fetch).
 * NON-retriable: 4xx (auth/validation) — thrown immediately.
 *
 * Each attempt gets a fresh AbortController — timeout from attempt N does not
 * kill attempt N+1.
 */
async function fetchWithRetry(
  url: string,
  init: Omit<RequestInit, "signal">,
  timeoutMs: number,
  label: string,
): Promise<Response> {
  let lastErr: unknown;

  for (let attempt = 1; attempt <= RETRY_MAX_ATTEMPTS; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const resp = await fetch(url, { ...init, signal: controller.signal });
      clearTimeout(timeoutId);

      const isRetriable =
        resp.status === 408 || resp.status === 429 || resp.status >= 500;

      if (isRetriable && attempt < RETRY_MAX_ATTEMPTS) {
        const retryAfter = resp.headers.get("retry-after");
        const retryAfterMs = retryAfter ? (parseInt(retryAfter, 10) || 0) * 1000 : 0;
        const backoff = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
        const jitter = Math.floor(Math.random() * 500);
        const delayMs = Math.max(retryAfterMs, backoff + jitter);
        logDebug(
          "OpenAI",
          `${label} retry ${attempt}/${RETRY_MAX_ATTEMPTS}: HTTP ${resp.status}, czekam ${delayMs}ms`,
        );
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }

      return resp;
    } catch (err: unknown) {
      clearTimeout(timeoutId);
      lastErr = err;

      const isAbort = err instanceof Error && err.name === "AbortError";
      const isNetwork = err instanceof TypeError;
      const isRetriable = isAbort || isNetwork;

      if (isRetriable && attempt < RETRY_MAX_ATTEMPTS) {
        const delayMs =
          RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1) + Math.floor(Math.random() * 500);
        const reason = isAbort ? `timeout ${timeoutMs / 1000}s` : "network";
        logDebug(
          "OpenAI",
          `${label} retry ${attempt}/${RETRY_MAX_ATTEMPTS}: ${reason}, czekam ${delayMs}ms`,
        );
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }

      if (isAbort) {
        throw new Error(
          `OpenAI ${label} timeout — brak odpowiedzi w ciagu ${timeoutMs / 1000}s (po ${attempt} probach).`,
        );
      }
      throw err;
    }
  }

  throw lastErr;
}

/**
 * Build clean OpenAI API request body.
 * - Reasoning models: reasoning_effort only, no temperature/top_p/max_tokens
 * - Standard models: temperature only, no reasoning_effort, no max_tokens
 * - Never send max_tokens — let the model decide output length
 */
function buildRequestBody(
  model: string,
  messages: Array<{ role: string; content: string }>,
  options?: LLMOptions,
): Record<string, unknown> {
  const body: Record<string, unknown> = { model, messages };

  if (isReasoningModel(model)) {
    body.reasoning_effort = options?.reasoningEffort || "medium";
  } else {
    body.temperature = options?.temperature ?? DEFAULT_TEMPERATURE;
    // Standard models can have max_tokens if explicitly set (prevents runaway output)
    if (options?.maxTokens) {
      body.max_tokens = options.maxTokens;
    }
  }

  // Structured JSON output (OpenAI format)
  if (options?.jsonSchema) {
    body.response_format = {
      type: "json_schema",
      json_schema: {
        name: "structured_output",
        strict: true,
        schema: options.jsonSchema,
      },
    };
  }

  return body;
}

/**
 * Build clean error message z error response — handles gateway HTML bodies.
 *
 * Cloudflare/AWS/nginx czasem zwracaja HTML error pages (502/504/523) zamiast
 * JSON gdy upstream (OpenAI) timeout'uje lub gateway sam pada. Pre-fix:
 * `await response.json()` na takim body throws `Unexpected token '<'` —
 * cryptic error wymagajacy zaglebienia w kod. Post-fix: detect HTML/non-JSON
 * body, zwroc explicit message ze status + preview.
 *
 * Pipeline nadal fail (nic nie zrobimy gdy gateway pada), ale forensics
 * czytelne: "OpenAI Responses API error: 504 - gateway non-JSON body (1283
 * chars): <html>504 Gateway Timeout..." zamiast cryptic JSON parse error.
 */
async function buildOpenAIErrorMessage(response: Response, label: string): Promise<string> {
  let bodyText = "";
  try {
    bodyText = await response.text();
  } catch {
    return `${label} error: ${response.status} - (response body read failed)`;
  }
  const trimmed = bodyText.trim();
  const looksLikeJson = trimmed.startsWith("{") || trimmed.startsWith("[");
  if (looksLikeJson) {
    try {
      const parsed = JSON.parse(trimmed);
      const msg = parsed?.error?.message || parsed?.message || "Unknown";
      return `${label} error: ${response.status} - ${msg}`;
    } catch {
      return `${label} error: ${response.status} - malformed JSON body (${trimmed.length} chars)`;
    }
  }
  // Non-JSON body — gateway HTML error page (Cloudflare/AWS/nginx 5xx)
  const preview = trimmed.slice(0, 200).replace(/\s+/g, " ");
  return `${label} error: ${response.status} - gateway non-JSON body (${trimmed.length} chars): ${preview}`;
}

export const openaiProvider: LLMProvider = {
  type: "openai",

  /**
   * Responses API — for reasoning models with chaining (draft generation).
   * Uses /v1/responses endpoint with previous_response_id for multi-block continuity.
   */
  async callResponses(input: string, options: ResponsesAPIOptions): Promise<LLMResponse> {
    const model = options?.model || DEFAULT_MODEL;
    const timeoutMs = options?.timeoutMs || DEFAULT_TIMEOUT_MS;
    const tStart = Date.now();

    const body: Record<string, unknown> = {
      model,
      input,
      reasoning: { effort: options?.reasoningEffort || "medium" },
      text: { format: { type: "text" } },
    };

    if (options?.instructions) {
      body.instructions = options.instructions;
    }
    if (options?.previousResponseId) {
      body.previous_response_id = options.previousResponseId;
    }
    if (options?.tools && options.tools.length > 0) {
      body.tools = options.tools;
    }

    logDebug("OpenAI", `Responses: model=${model}, chaining=${!!options?.previousResponseId}, tools=${options?.tools?.map(t => t.type).join(",") || "none"}`);

    const target = getApiTarget("openai", "/v1/responses");
    const response = await fetchWithRetry(
      target.url,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...target.headers },
        body: JSON.stringify(body),
      },
      timeoutMs,
      "Responses API",
    );

    if (!response.ok) {
      throw new Error(await buildOpenAIErrorMessage(response, "OpenAI Responses API"));
    }

    const result = await response.json();

    // Verbose-only: response structure dumps (OpenAI metadata keys + output array items).
    // Widoczne TYLKO przy VITE_LOG_LEVEL=verbose — zaśmiecały debug smoke testy.
    logVerbose("OpenAI", `Responses keys: ${Object.keys(result).join(",")}`, { output_text_type: typeof result.output_text, output_text_length: result.output_text?.length || 0 });
    if (result.output && Array.isArray(result.output)) {
      logVerbose("OpenAI", `Responses output[] length: ${result.output.length}`);
      for (const item of result.output.slice(0, 2)) {
        logVerbose("OpenAI", `Responses output item`, { type: item.type, role: item.role, contentLength: JSON.stringify(item.content || item.text || "").length });
      }
    }

    // Extract text: try output_text first, then dig into output[] array
    let text = result.output_text?.trim() || "";
    if (!text && result.output && Array.isArray(result.output)) {
      // Responses API may return text in output[].content[].text
      for (const item of result.output) {
        if (item.type === "message" && Array.isArray(item.content)) {
          for (const part of item.content) {
            if (part.type === "output_text" || part.type === "text") {
              text += (part.text || "") + "\n";
            }
          }
        }
      }
      text = text.trim();
    }

    const responseId = result.id || "";
    const tokensInput = result.usage?.input_tokens || 0;
    const tokensOutput = result.usage?.output_tokens || 0;
    const cachedTokens = result.usage?.input_tokens_details?.cached_tokens || 0;
    const reasoningTokens = result.usage?.output_tokens_details?.reasoning_tokens || 0;
    const durationMs = Date.now() - tStart;
    const costUsd = estimateCost(model, tokensInput, tokensOutput);

    logDebug("OpenAI", `Responses resp_id=${responseId?.slice(0, 25)}, ${text.length} chars, ${tokensInput}+${tokensOutput} tokens`);

    trackLlmCall({
      provider: "openai",
      model,
      step: options?.step,
      tokensInput, tokensOutput, cachedTokens, reasoningTokens,
      costUsd, durationMs,
      mapId: options?.mapId, nodeId: options?.nodeId, runId: options?.runId,
    });

    return {
      text,
      tokensInput,
      tokensOutput,
      modelUsed: model,
      provider: "openai",
      costUsd,
      cachedTokens,
      reasoningTokens,
      durationMs,
      responseId,
    };
  },

  /**
   * Chat Completions API — for standard models (KG, FanOut, Outline, Optimization, etc.)
   */
  async call(prompt: string, options: LLMCallOptions): Promise<LLMResponse> {
    const model = options?.model || DEFAULT_MODEL;
    const timeoutMs = options?.timeoutMs || DEFAULT_TIMEOUT_MS;
    const tStart = Date.now();

    const messages: Array<{ role: string; content: string }> = [];
    if (options?.systemPrompt) {
      messages.push({ role: "system", content: options.systemPrompt });
    }
    messages.push({ role: "user", content: prompt });

    const body = buildRequestBody(model, messages, options);

    const target = getApiTarget("openai", "/v1/chat/completions");
    const response = await fetchWithRetry(
      target.url,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...target.headers },
        body: JSON.stringify(body),
      },
      timeoutMs,
      "Chat Completions",
    );

    if (!response.ok) {
      throw new Error(await buildOpenAIErrorMessage(response, "OpenAI Chat Completions"));
    }

    const result = await response.json();
    const choice = result.choices?.[0];
    const text = choice?.message?.content?.trim() || "";
    const finishReason = choice?.finish_reason as string | undefined;
    const tokensInput = result.usage?.prompt_tokens || 0;
    const tokensOutput = result.usage?.completion_tokens || 0;
    const cachedTokens = result.usage?.prompt_tokens_details?.cached_tokens || 0;
    const reasoningTokens = result.usage?.completion_tokens_details?.reasoning_tokens || 0;
    const durationMs = Date.now() - tStart;
    const costUsd = estimateCost(model, tokensInput, tokensOutput);

    // Rzuc blad jesli model nie skonczyl naturalnie — zapobiega cichemu zwracaniu
    // uciętego JSON-a ktory potem wywali parser w niezrozumialym miejscu.
    // finish_reason: "stop" = OK, "length" = hit max_tokens, "content_filter" = odmowa,
    // "tool_calls" = wywolano tool, null/undefined = streaming abort.
    if (finishReason && finishReason !== "stop" && finishReason !== "tool_calls") {
      throw new Error(
        `OpenAI finish_reason="${finishReason}" (model ${model}, ${tokensOutput}/${options?.maxTokens ?? "∞"} tok). ` +
        `Response ${finishReason === "length" ? "OBCIETA — podnies maxTokens" : "odrzucona przez model"}.`
      );
    }

    trackLlmCall({
      provider: "openai",
      model,
      step: options?.step,
      tokensInput, tokensOutput, cachedTokens, reasoningTokens,
      costUsd, durationMs,
      mapId: options?.mapId, nodeId: options?.nodeId, runId: options?.runId,
    });

    return {
      text,
      tokensInput,
      tokensOutput,
      modelUsed: model,
      provider: "openai",
      costUsd,
      cachedTokens,
      reasoningTokens,
      durationMs,
    };
  },
};
